<template>
    <div class="table-log">
        <CompassAlert
            v-if="getImportsList.length === 0"
            class="table-log__alert"
            type="neutral"
            :description="$t.__('common.labels.no_import_maded')"
            :showClose="false"
        />
        <div v-else>
            <table ref="importLogs" class="table-log__table">
                <thead>
                    <tr>
                        <th
                            v-for="(col, index) in columns"
                            :key="index"
                            :class="col.class"
                            class="typographies-paragraph-medium-bold"
                        >
                            {{ col.label }}
                        </th>
                    </tr>
                </thead>
                <tbody ref="tableLogContent" @scroll="onTableContentScroll">
                    <tr
                        v-for="(log, index) in getImportsList"
                        :key="log.id"
                        :ref="'tableTrItem' + index"
                    >
                        <td class="table-log__date">
                            <p class="typographies-paragraph-medium-regular">
                                {{ getDateFormatted(log.created_at, 'DD/MM/YYYY') }}
                            </p>
                            <p class="table-log__minor-date typographies-paragraph-small-regular">
                                {{ getDateFormatted(log.created_at, 'HH:mm') }}
                            </p>
                        </td>
                        <td class="table-log__type typographies-paragraph-medium-regular">
                            {{ getTypeOfImport(log.type) }}
                        </td>
                        <td class="table-log__status typographies-paragraph-medium-regular">
                            <ImportTag :label="getImportStatus(log)" :status="log.status" />
                        </td>
                        <td class="table-log__user typographies-paragraph-medium-regular">
                            {{ getFullNameStaff(log) }}
                        </td>
                        <td class="table-log__processed typographies-paragraph-medium-regular">
                            <span>
                                {{ getLabelForLine(log, log.processed_rows, 'log') }}
                            </span>
                        </td>
                        <td class="table-log__invalid typographies-paragraph-medium-regular">
                            <span>
                                {{ getLabelForLine(log, log.invalid_rows, 'log') }}
                            </span>
                        </td>
                        <td class="table-log__action typographies-paragraph-small-semibold">
                            <div>
                                <CompassTooltip
                                    contentClass="tooltip-settings-import"
                                    :disabled="isImportCanBeReverted(log)"
                                    :delay="{ show: 500, hide: 0 }"
                                    :content="getRevertImportBtnLabel(log)"
                                >
                                    <CompassButton
                                        v-if="log.is_revertible && log.processed_rows > 0"
                                        ref="cancelImportBtn"
                                        :label="$t.__('common.actions.cancel')"
                                        type="tertiary-destructive"
                                        :disabled="!isImportCanBeReverted(log)"
                                        size="small"
                                        @click="onRevert(log)"
                                    />
                                </CompassTooltip>

                                <CompassButton
                                    v-if="isImportCancellable(log)"
                                    type="tertiary-destructive"
                                    size="small"
                                    :label="$t.__('common.actions.cancel')"
                                    @click="onCancel(log)"
                                />

                                <CompassButton
                                    :label="$t.__('settings.import.ImportLogs.see')"
                                    type="secondary"
                                    size="small"
                                    @click="onDisplayDetail(log.id)"
                                />
                            </div>
                        </td>
                    </tr>
                    <tr v-if="isLoadingMoreNeeded" class="table-log__loader">
                        <td colspan="7">
                            <CompassLoader color="common-icon-neutral" />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import ImportTag from 'vcomponents/settings/import/ImportTag.vue';
import ImportStatusDictionary from 'Dictionaries/imports/ImportStatusDictionary.js';
import { createNamespacedHelpers } from 'vuex';
import ImportCancelPopup from 'vcomponents/settings/import/ImportCancelPopup.vue';
import importsMixins from 'vcomponents/settings/import/importsMixins.js';
import { importStatus } from 'stores/settings/settingsImportsStore';
const importsStore = createNamespacedHelpers('importsStore');
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    components: { ImportTag, ImportCancelPopup },
    mixins: [importsMixins],
    data() {
        return {
            lineHeight: null,
            isFetchingMoreItems: false,
            columns: [
                {
                    class: 'table-log__date',
                    label: this.$l10n.__('Date'),
                    sort: 'created_at',
                },
                {
                    class: 'table-log__type',
                    label: this.$l10n.__('Type'),
                    sort: 'type',
                },
                {
                    class: 'table-log__status',
                    label: this.$l10n.__('Statut'),
                    sort: 'status',
                },
                {
                    class: 'table-log__user',
                    label: this.$l10n.__('Utilisateur'),
                    sort: 'user',
                },
                {
                    class: 'table-log__processed',
                    label: this.$l10n.__('Lignes traitées'),
                    sort: 'processed_rows',
                },
                {
                    class: 'table-log__invalid',
                    label: this.$l10n.__('Lignes en erreurs'),
                    sort: 'invalid_rows',
                },
                {
                    class: 'table-log__action',
                    label: '',
                    sort: 'actions',
                },
            ],
        };
    },
    computed: {
        ...importsStore.mapGetters(['getImportsList', 'getImportsListPagination']),
        isLoadingMoreNeeded() {
            return this.getImportsList.length > 10;
        },
    },
    created() {
        const params = new Proxy(new URLSearchParams(window.location.search), {
            get: (searchParams, prop) => searchParams.get(prop),
        });

        if (params.import) {
            this.onDisplayDetail(+params.import);
        }
    },
    mounted() {
        this.lineHeight = this.$refs.tableTrItem0[0]?.clientHeight;
        this.$refs.tableLogContent.style.maxHeight = `${this.lineHeight * 10.5}px`;
    },
    methods: {
        ...popupPanelMapActions(['open']),
        ...importsStore.mapActions([
            'fetchImportsList',
            'updateIsRestartImportNeed',
            'updateImportUnlimited',
            'updateNonGoldMaxLines',
        ]),
        getDateFormatted(date, formatter) {
            return moment(date).format(formatter);
        },
        getImportStatus(log) {
            let statusLabel = ImportStatusDictionary[log.status]?.status ?? '';
            if (
                [importStatus.REVERTING, importStatus.PROCESSING].includes(log.status) &&
                log.total_rows > 0
            ) {
                const percentProgress = Math.round((log.browsed_rows / log.total_rows) * 100);
                statusLabel = `${statusLabel} (${percentProgress} %)`;
            }
            return statusLabel;
        },
        getTypeOfImport(type) {
            return (
                this.getMetadataImport.find((item) => item.type === type)?.title ??
                this.$l10n.__('Autre')
            );
        },
        onDisplayDetail(importId) {
            this.open({
                key: 'importDetailPopup',
                props: {
                    importId: importId,
                },
            });
        },
        onCancel({ type, batch_id }) {
            this.open({
                key: 'importCancelPopup',
                props: {
                    batchId: batch_id,
                    type,
                },
            });
        },
        onRevert(importToRevert) {
            this.open({
                key: 'importRevertPopup',
                props: {
                    importToRevert,
                },
            });
        },
        onTableContentScroll(event) {
            const scrollMarginFromEnd = Math.abs(
                event.target.scrollHeight - event.target.scrollTop - event.target.clientHeight,
            );
            if (!this.isFetchingMoreItems) {
                if (scrollMarginFromEnd < this.lineHeight / 2) {
                    if (this.getImportsList.length < this.getImportsListPagination.total) {
                        this.loadMoreImportItem();
                    } else {
                        setTimeout(() => {
                            this.setScrollbarPositionBeforeLoader();
                        }, 1000);
                    }
                }
            }
        },
        loadMoreImportItem() {
            this.isFetchingMoreItems = true;
            this.fetchImportsList()
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        text: this.$l10n.__(
                            "Une erreur est survenue lors de la récupération de l'historique des imports.",
                        ),
                        icon: true,
                    });
                })
                .finally(() => {
                    this.isFetchingMoreItems = false;
                    this.setScrollbarPositionBeforeLoader();
                });
        },
        setScrollbarPositionBeforeLoader() {
            if (this.$refs.tableLogContent) {
                this.$refs.tableLogContent.scrollTop =
                    this.$refs.tableLogContent.scrollHeight -
                    this.$refs.tableLogContent.clientHeight -
                    this.lineHeight;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;
.table-log {
    width: 100%;
    .table-log__alert {
        margin-bottom: 0;
    }
    .table-log__table {
        background: var(--colors-common-background-default);
        border: 1px solid var(--colors-common-border-default);
        border-radius: var(--spacing-0) var(--spacing-0) 0 0;
        display: block;
        table-layout: fixed;
        th {
            padding: var(--spacing-0) var(--spacing-3);
            text-align: left;
        }
        td {
            border-bottom: 1px solid var(--colors-common-border-default);
            padding: var(--spacing-1) var(--spacing-2);
        }
        thead {
            border-bottom: 1px solid var(--colors-common-border-default);
            border-spacing: 0;
            display: inline-block;
            width: 100%;
        }
        tbody {
            border-radius: 0 0 var(--spacing-0) var(--spacing-0);
            border-spacing: 0;
            border-top: none;
            display: inline-block;
            overflow-y: auto;
            width: 100%;
            tr:last-child {
                td {
                    border: none !important;
                }
            }
            p {
                margin: 0;
            }
        }
        .table-log__date {
            min-width: 100px;
        }
        .table-log__type {
            min-width: 120px;
        }
        .table-log__status {
            min-width: 235px;
        }
        .table-log__user {
            min-width: 128px;
        }
        .table-log__processed {
            min-width: 108px;
        }
        .table-log__invalid {
            min-width: 122px;
        }
        .table-log__action {
            width: 100%;
            div {
                align-items: center;
                display: flex;
                flex-direction: row;
                justify-content: right;
                gap: var(--spacing-1);
            }
        }
        .table-log__actions {
            gap: var(--spacing-1);
        }
        .table-log__loader {
            td {
                padding: var(--spacing-3) !important;
                text-align: center;
            }
        }
        .table-log__minor-date {
            color: var(--colors-common-text-subtle-default);
        }
    }
}
</style>
